/**
 * The admin section's table system.
 *
 * Promoted out of `AdminPricingScreen/pricingUi.js` once a second screen
 * (Guidelines) needed the same tables — per ADMIN_UI.md, the pricing screen's
 * primitives move here the moment they stop being one screen's business.
 * `pricingUi` re-exports these under its own names, so the pricing call sites
 * are unchanged and there is exactly one definition of the look.
 *
 * What a table on an admin screen is:
 *   - a bordered, rounded block sized to the rows it actually has;
 *   - capped at `maxRows`, with a hover-revealed expander along the bottom edge
 *     that drops the cap and renders everything;
 *   - small-caps faint headers, hairline row rules, tabular figures.
 */
import { cloneElement, isValidElement, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";

import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import { ink, level1, slate, teal } from "../../../theme/colors";

export const GRID_HEADER_H = 34;
export const GRID_ROW_H = 36;
const EXPANDER_H = 30;

// The grid-relevant slice of the admin palette, aliased onto the color system
// so it can never drift from `pricingUi.T` / `adminTokens.A`.
const G = {
  ink,
  ink2: slate[700],
  faint: slate[400],
  line: "#e5e7eb",
  lineSoft: "#eef2f6",
  surface: "#ffffff",
  surface2: slate[50],
  accent: level1.text,
  accentSoft: teal[50],
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  sans: "Inter, sans-serif",
};

/**
 * Height that fits the rows exactly, up to `maxRows`, then scrolls. AG Grid
 * needs an explicit height from its container, and a fixed one leaves a slab of
 * empty grid under a four-row table — this sizes to the content instead.
 */
export const gridHeight = (rowCount, maxRows = 12) =>
  `${GRID_HEADER_H + Math.max(1, Math.min(rowCount || 0, maxRows)) * GRID_ROW_H + 2}px`;

/**
 * The house table look: small-caps faint headers, hairline row rules, no column
 * separators, and monospaced tabular figures in any column marked
 * `cellClass: "num"` so decimal points line up down the column.
 */
export const ADMIN_GRID_SX = {
  "--ag-header-height": `${GRID_HEADER_H}px`,
  "--ag-row-height": `${GRID_ROW_H}px`,
  "--ag-border-color": G.line,
  "--ag-header-background-color": "transparent",
  "--ag-header-column-separator-display": "none",
  "--ag-row-hover-color": G.surface2,
  "--ag-selected-row-background-color": G.accentSoft,
  // Breathing room inside the grid's own border box (AdminGrid draws one and
  // insets it to the section title's gutter).
  "--ag-cell-horizontal-padding": "20px",
  "--ag-odd-row-background-color": "transparent",
  // AG Grid reserves a min-height on the row viewport so its "no rows" overlay
  // has somewhere to sit. `domLayout: "autoHeight"` does NOT clear it, so a
  // one-row table still rendered a slab of empty grid under the row. These
  // screens draw their own empty states, so the reservation is dead weight.
  // Doubled `&&` to out-specify the theme's own rule without `!important`.
  "&& .ag-center-cols-viewport, && .ag-body-viewport, && .ag-center-cols-container":
    { minHeight: 0 },
  ".ag-header": { borderBottom: `1px solid ${G.line}` },
  ".ag-header-cell-text": {
    fontSize: "10.5px",
    fontWeight: 600,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color: G.faint,
  },
  // Zeroing the viewport min-height above must not let a lone row collapse with
  // it — a row keeps its full height whether there is one of them or fifty.
  ".ag-row": {
    borderBottom: `1px solid ${G.lineSoft}`,
    minHeight: `${GRID_ROW_H}px`,
  },
  ".ag-header-row": { minHeight: `${GRID_HEADER_H}px` },
  ".ag-row-last": { borderBottom: "none" },
  ".ag-cell": { fontSize: "12.5px", color: G.ink },
  // `text-align` is what actually moves a cell's text: `.ag-cell` is a BLOCK
  // element, so `justify-content` alone did nothing to it while the header's
  // label (which IS flex) moved — that's how header and cells drifted apart.
  // Both properties are set so plain-text cells and cellRenderer cells (which
  // AG Grid wraps in a flex `.ag-cell-wrapper`) land the same way.
  ".ag-cell.num": {
    fontFamily: G.mono,
    fontVariantNumeric: "tabular-nums",
    textAlign: "right",
    justifyContent: "flex-end",
  },
  ".ag-cell.num .ag-cell-wrapper, .ag-cell.num .ag-cell-value": {
    justifyContent: "flex-end",
    width: "100%",
  },
  ".ag-header-cell.num .ag-header-cell-label": { justifyContent: "flex-end" },
  ".ag-cell.key": {
    fontFamily: G.mono,
    fontSize: "11.5px",
    color: G.ink2,
    textAlign: "left",
  },
  ".ag-header-cell.key .ag-header-cell-label": { justifyContent: "flex-start" },
  ".ag-cell.ctr": {
    fontFamily: G.mono,
    fontVariantNumeric: "tabular-nums",
    textAlign: "center",
    justifyContent: "center",
  },
  ".ag-cell.ctr .ag-cell-wrapper, .ag-cell.ctr .ag-cell-value": {
    justifyContent: "center",
    width: "100%",
  },
  ".ag-header-cell.ctr .ag-header-cell-label": { justifyContent: "center" },
  // An empty cell in a row the desk just added is a prompt, not a value — the
  // "—" the other columns use would read as "intentionally blank".
  ".ag-cell.ph": { color: G.faint, fontStyle: "italic", fontFamily: G.sans },
  // Action columns hold buttons, not text. The 20px cell inset would squeeze an
  // icon button past the column edge, and AG Grid answers overflow with an
  // ellipsis — so these cells drop the padding and centre their contents.
  ".ag-cell.act": {
    padding: 0,
    textAlign: "center",
    justifyContent: "center",
    overflow: "visible",
    textOverflow: "clip",
  },
  // STRETCH, not centre, and an explicit height down the whole wrapper chain:
  // an action control fills its cell, and `height: 100%` on the control can only
  // resolve if every box above it has a height to resolve against.
  ".ag-cell.act .ag-cell-wrapper, .ag-cell.act .ag-cell-value": {
    overflow: "visible",
    textOverflow: "clip",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  ".ag-header-cell.act": { padding: 0 },
  ".ag-header-cell.act .ag-header-cell-label": { justifyContent: "center" },
  // Editable cells read as fields without a box around every one.
  ".ag-cell-inline-editing": {
    height: `${GRID_ROW_H}px`,
    borderColor: G.accent,
  },
};

export const ADMIN_GRID_COL_DEF = {
  sortable: true,
  resizable: true,
  suppressHeaderMenuButton: true,
  suppressHeaderFilterButton: true,
  suppressMovable: true,
};

/**
 * The hover-revealed "show everything" control along the bottom edge of a
 * height-capped block. Collapsed it fades the clipped content out under itself;
 * expanded it sits on its own strip in padding the caller reserves.
 */
// `surface` is the colour the fade resolves to — a block on a dark panel needs
// its own, or the gradient reads as a white smear across it.
export const Expander = ({
  expanded,
  onToggle,
  label,
  collapseLabel,
  surface = G.surface,
  color = G.faint,
}) => (
  <Box
    component="button"
    type="button"
    className="admin-expander"
    onClick={onToggle}
    sx={{
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: EXPANDER_H,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      border: "none",
      cursor: "pointer",
      font: "inherit",
      fontSize: 11.5,
      fontWeight: 600,
      color,
      background: expanded
        ? surface
        : `linear-gradient(180deg, ${surface}00 0%, ${surface} 65%)`,
      opacity: expanded ? 1 : 0,
      transition: "opacity .15s",
      "&:hover": { color: G.ink },
      "&:focus-visible": { opacity: 1 },
    }}
  >
    <ChevronRightRounded
      sx={{
        fontSize: 17,
        transform: expanded ? "rotate(-90deg)" : "rotate(90deg)",
      }}
    />
    {expanded ? collapseLabel || "Collapse" : label}
  </Box>
);

Expander.propTypes = {
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  label: PropTypes.node,
  collapseLabel: PropTypes.node,
  surface: PropTypes.string,
  color: PropTypes.string,
};

/** A non-table block capped to `collapsedMaxHeight` with the same expander. */
export const ExpandableBlock = ({
  collapsedMaxHeight = 260,
  label,
  surface,
  color,
  children,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        position: "relative",
        pb: expanded ? `${EXPANDER_H}px` : 0,
        "&:hover .admin-expander": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          maxHeight: expanded ? "none" : collapsedMaxHeight,
          overflow: expanded ? "visible" : "hidden",
        }}
      >
        {children}
      </Box>
      <Expander
        expanded={expanded}
        onToggle={() => setExpanded((e) => !e)}
        label={label || "Show all"}
        surface={surface}
        color={color}
      />
    </Box>
  );
};

ExpandableBlock.propTypes = {
  collapsedMaxHeight: PropTypes.number,
  label: PropTypes.node,
  surface: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node,
};

/**
 * An admin table: a bordered rounded block, inset from the card edge so it lines
 * up with the section title above it, sized to its rows.
 *
 * The border lives on the wrapper rather than on the rows: the last row drops
 * its own bottom border (see ADMIN_GRID_SX) so the two can't stack into a 2px
 * line when the rows happen to fill the grid exactly.
 */
export const AdminGrid = ({
  rowCount = 0,
  maxRows = 12,
  // Drops the built-in inset for a grid whose parent already pads it — the
  // border and radius stay either way.
  flush,
  sx,
  children,
}) => {
  const [expanded, setExpanded] = useState(false);
  const capped = rowCount > maxRows;

  // Below the cap the GRID measures itself (`domLayout: "autoHeight"`) rather
  // than being told a computed height. A computed one is always slightly wrong:
  // it can't know about a horizontal scrollbar, and it assumes every row is
  // exactly GRID_ROW_H — so a short table came out a scrollbar too short and
  // clipped its own last row. Only a capped, collapsed table needs a fixed
  // height, because that is the one case that has to scroll internally.
  const measured = expanded || !capped;
  const visibleRows = Math.max(Math.min(rowCount, maxRows), 1);
  const grid =
    isValidElement(children) && measured
      ? cloneElement(children, { domLayout: "autoHeight" })
      : children;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${G.line}`,
        borderRadius: "8px",
        // Inset on ALL sides: a card header (AdminCard draws a rule under its
        // own) needs air above the table, not just beside it.
        ...(flush ? null : { mt: 2, mx: 2.5, mb: 2.5 }),
        // When expanded the control sits below the last row instead of over it.
        pb: expanded ? `${EXPANDER_H}px` : 0,
        "&:hover .admin-expander": { opacity: 1 },
      }}
    >
      <AgGridTableWrapper
        compactTable
        noBorder
        height={
          measured
            ? "auto"
            : `${GRID_HEADER_H + visibleRows * GRID_ROW_H + 2}px`
        }
        sx={{ ...ADMIN_GRID_SX, ...sx }}
      >
        {grid}
      </AgGridTableWrapper>

      {capped ? (
        <Expander
          expanded={expanded}
          onToggle={() => setExpanded((e) => !e)}
          label={`Show all ${rowCount} rows`}
        />
      ) : null}
    </Box>
  );
};

AdminGrid.propTypes = {
  rowCount: PropTypes.number,
  maxRows: PropTypes.number,
  flush: PropTypes.bool,
  sx: PropTypes.object,
  children: PropTypes.node,
};

export default AdminGrid;
